import { clamp01 } from '../core/math';

/**
 * The mix as data (pure, no Web Audio): bus levels, the listening environment (street / car / indoors),
 * ducking depths, variation (pitch / level jitter, round-robin takes) and the safety clipper's curve.
 * `mixer.ts` builds the graph from these; docs/AUDIO.md has the table and the reasoning.
 */

/** Per-bus trim into the groups (linear, pre-volume-slider). */
export const BUS_LEVELS = {
  /** Every car engine + tyres (the player's car and the four nearest). */
  engine: 1.0,
  /** Police two-tones: loud by nature, already distance-scaled per voice. */
  siren: 0.8,
  /** Impacts, props, doors, footsteps, ZzFX world one-shots. */
  sfx: 1.0,
  /** Gunshots, reloads, swings and blows: the loudest transients, trimmed so the limiter only catches stacks. */
  weapons: 0.82,
  /** Human voices in the world: grunts, screams, police whistles and shouts. */
  voice: 0.9,
  /** Menus, phone, HUD (plays under pause). */
  ui: 0.9,
  /** The soundscape beds and far events. */
  ambience: 0.8,
  /** The radio / jukebox, a touch over unity so a station sits level with the engine at cruise. */
  music: 1.3,
} as const;

export type MixBus = keyof typeof BUS_LEVELS;

/** Where the listener is: out on the street, inside a car, or in a room drawn instead of the city. */
export type Environment = 'street' | 'car' | 'interior';

export interface EnvironmentTone {
  /** Gain on the ambience beds + far events. */
  ambGain: number;
  /** Lowpass on the ambience (Hz): the cabin / the walls take the top off. */
  ambCutoff: number;
  /** Gain on the other cars' engines + sirens (the player's own engine is not in a room). */
  vehGain: number;
  vehCutoff: number;
  /** Time constant (s) for moving into this environment. */
  tc: number;
}

const ENVIRONMENTS: Record<Environment, EnvironmentTone> = {
  street: { ambGain: 1, ambCutoff: 18000, vehGain: 1, vehCutoff: 18000, tc: 0.35 },
  // A 1970 saloon cabin: the street is still there, just darker. The radio and the engine take over.
  car: { ambGain: 0.85, ambCutoff: 3200, vehGain: 1, vehCutoff: 18000, tc: 0.25 },
  // Behind brick walls (the hideout, Sid's back room, the spray studio): the city as a dull murmur.
  interior: { ambGain: 0.3, ambCutoff: 520, vehGain: 0.35, vehCutoff: 650, tc: 0.45 },
};

export function environmentTone(env: Environment): EnvironmentTone {
  return ENVIRONMENTS[env];
}

/** Interior wins over the car (you can drive into the spray studio); otherwise the car over the street. */
export function environmentFor(driving: boolean, interior: boolean): Environment {
  return interior ? 'interior' : driving ? 'car' : 'street';
}

/** Radio level under a line of dialogue (1 = silent) and the ambience dip with it. */
export const DIALOGUE_DUCK = { music: 0.45, ambience: 0.2 } as const;

/**
 * How far the radio and the ambience sit back for the loudest siren (its voice level, 0..0.55 at the
 * car). Starts once a siren is close enough to matter (~60 m), deepest when it is on top of you.
 */
export function sirenDuck(level: number): { music: number; ambience: number } {
  const x = clamp01((level - 0.2) / (0.5 - 0.2));
  const s = x * x * (3 - 2 * x);
  return { music: 0.38 * s, ambience: 0.3 * s };
}

/** Radio dip during a pursuit by heat level (0 below 2). */
export function heatDuck(heat: number): number {
  return heat >= 2 ? 0.15 + 0.08 * Math.min(3, heat - 2) : 0;
}

// ---- Variation ------------------------------------------------------------------------------------

export function centsToRatio(cents: number): number {
  return Math.pow(2, cents / 1200);
}

/** A pitch ratio within +-`cents` (uniform in cents). */
export function jitterRatio(cents: number, rand: () => number = Math.random): number {
  return centsToRatio((rand() * 2 - 1) * cents);
}

/** A level multiplier within +-`db` dB. */
export function jitterGain(db: number, rand: () => number = Math.random): number {
  return Math.pow(10, ((rand() * 2 - 1) * db) / 20);
}

/**
 * Picks one of `n` takes at random but never the same one twice running (a repeated identical take is
 * what the ear catches as "machine-gun" repetition). Pure given `rand`.
 */
export class RoundRobin {
  private last = -1;
  constructor(private readonly n: number) {}
  next(rand: () => number = Math.random): number {
    if (this.n <= 1) return 0;
    let i = Math.floor(rand() * (this.n - 1));
    if (i >= this.last && this.last >= 0) i++;
    this.last = Math.min(this.n - 1, i);
    return this.last;
  }
}

// ---- Output safety --------------------------------------------------------------------------------

/** Linear below this (about -1 dBFS), a soft knee above, never past 1. */
export const CLIP_KNEE = 0.89;

/** The last-ditch soft clipper after the limiter: transparent under `CLIP_KNEE`, tanh-rounded over it. */
export function softClip(x: number): number {
  const a = Math.abs(x);
  if (a <= CLIP_KNEE) return x;
  const room = 1 - CLIP_KNEE;
  return Math.sign(x) * (CLIP_KNEE + room * Math.tanh((a - CLIP_KNEE) / room));
}

export function softClipCurve(n = 1024): Float32Array {
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) c[i] = softClip((i / (n - 1)) * 2 - 1);
  return c;
}

/** One-shot voice budget by render tier (a phone on LOW is short of CPU for the audio thread too). */
export function voiceBudget(tier: 'low' | 'medium' | 'high'): number {
  return tier === 'low' ? 24 : 40;
}

/** Other cars' engine voices by tier (the player's own car always has one). */
export function otherEngines(tier: 'low' | 'medium' | 'high'): number {
  return tier === 'low' ? 2 : 4;
}
